import { useMemo } from 'react';
import { skipToken, useQuery } from '@tanstack/react-query';
import { fetchMatchExplanations } from 'services/sourcingSearch/api';
import {
  ExplanationContext,
  MatchExplanationsRequest,
  MatchExplanationsResponse
} from 'modules/candidate-profile/types/CandidateSearchFilter';

const EXPLANATIONS_STORAGE_KEY = 'match-explanations-cache';
// Each page is about 10 KB, and sessionStorage holds about 5 MB per origin.
const MAX_STORED_PAGES = 20;

type ExplanationResponsesByRequestKey = Record<string, unknown>;

const isExplanationsResponse = (
  value: unknown
): value is MatchExplanationsResponse =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as MatchExplanationsResponse).explanations === 'object' &&
  (value as MatchExplanationsResponse).explanations !== null;

// The query cache does not survive a reload, and the search it explains does
// (useVectorSearchCache). A new call would reword every row.
const readStored = (): ExplanationResponsesByRequestKey => {
  try {
    const parsed: unknown = JSON.parse(
      sessionStorage.getItem(EXPLANATIONS_STORAGE_KEY) ?? '{}'
    );
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as ExplanationResponsesByRequestKey)
      : {};
  } catch {
    return {};
  }
};

const store = (requestKey: string, response: MatchExplanationsResponse) => {
  // Insertion order, so the oldest page is dropped first.
  const entries = Object.entries({ ...readStored(), [requestKey]: response });
  try {
    sessionStorage.setItem(
      EXPLANATIONS_STORAGE_KEY,
      JSON.stringify(Object.fromEntries(entries.slice(-MAX_STORED_PAGES)))
    );
  } catch {
    // Storage full or blocked: the answers still show, they just don't persist.
  }
};

type UseSourcingExplanationsProps = {
  context: ExplanationContext | null;
  visibleCandidateIds: string[];
  rankOffset: number;
  /** Every result in rank order, so a sorted page is sent in rank order. */
  rankedIds?: string[];
};

/**
 * The visible rows in rank order, from the rank of the best one.
 *
 * The service reads the order of the ids as the ranking, so a page sorted by
 * name would make its first row "top-ranked". In rank order each row is
 * compared with rows truly above and below it, and only the real first is
 * "top-ranked". Unsorted, this is the visible page as it is.
 */
const toRankOrder = (
  visibleCandidateIds: string[],
  rankOffset: number,
  rankedIds?: string[]
) => {
  if (!rankedIds)
    return { candidateProfileIds: visibleCandidateIds, rankOffset };

  const rankOf = new Map(rankedIds.map((id, rank) => [id, rank]));
  const rank = (id: string) => rankOf.get(id) ?? Number.MAX_SAFE_INTEGER;
  const candidateProfileIds = [...visibleCandidateIds].sort(
    (left, right) => rank(left) - rank(right)
  );

  return {
    candidateProfileIds,
    rankOffset: rankOf.get(candidateProfileIds[0]) ?? rankOffset
  };
};

/**
 * Fetches match explanations for the page on screen, once per page.
 *
 * The query cache, not component state, holds the answers: a return to the
 * list or to an earlier page would otherwise pay for them again, and each
 * repeat comes back worded differently.
 */
export const useSourcingExplanations = ({
  context,
  visibleCandidateIds,
  rankOffset,
  rankedIds
}: UseSourcingExplanationsProps) => {
  const request: MatchExplanationsRequest | null =
    context && visibleCandidateIds.length > 0
      ? {
          id: context.searchId,
          ...toRankOrder(visibleCandidateIds, rankOffset, rankedIds),
          semanticQuery: context.semanticQuery,
          recruiterMessage: context.recruiterMessage
        }
      : null;

  const storageKey = JSON.stringify(request);

  const { data, isError, isPending } = useQuery({
    // The whole request: a chat turn keeps the session id but not the query.
    queryKey: ['matchExplanations', request],
    queryFn: request
      ? async () => {
          const response = await fetchMatchExplanations(request);
          store(storageKey, response);
          return response;
        }
      : skipToken,
    initialData: () => {
      const stored = request ? readStored()[storageKey] : undefined;
      // Anything else is not one of ours, so it is asked for again.
      return isExplanationsResponse(stored) ? stored : undefined;
    },
    staleTime: Infinity,
    retry: false
  });

  const isRequested = request !== null;

  return useMemo(() => {
    const isLoading = isRequested && isPending;
    const isAnswered = Boolean(data) || isError;

    return {
      // An id the response leaves out is answered too, or its cell loads forever.
      explanationsById: isAnswered
        ? Object.fromEntries(
            visibleCandidateIds.map((id) => [
              id,
              data?.explanations[id] ?? null
            ])
          )
        : {},
      pendingIds: new Set(isLoading ? visibleCandidateIds : [])
    };
  }, [isRequested, isPending, data, isError, visibleCandidateIds]);
};
